// =============================================================
// Autonomo - i18n System
// Primary language: English (en)
// =============================================================

import { Language } from '@prisma/client';

// Type-safe translation function
export type TranslationKey = string;

export type Translations = Record<string, unknown>;

// Language loading cache
const cache: Partial<Record<Language, Translations>> = {};

async function loadLanguage(lang: Language): Promise<Translations> {
  if (cache[lang]) return cache[lang]!;

  try {
    const translations = await import(`./${lang}.json`);
    cache[lang] = translations.default ?? translations;
    return cache[lang]!;
  } catch {
    // Fallback to English if language file not found
    if (lang !== 'en') {
      return loadLanguage('en');
    }
    return {};
  }
}

/**
 * Get a translation value by dot-notation key.
 * Example: t('auth.login.title', translations)
 */
export function t(key: TranslationKey, translations: Translations): string {
  const parts = key.split('.');
  let current: unknown = translations;

  for (const part of parts) {
    if (current === null || typeof current !== 'object') {
      return key; // Return key if path not found
    }
    current = (current as Record<string, unknown>)[part];
  }

  if (typeof current === 'string') return current;
  return key; // Return key as fallback
}

/**
 * Server-side: load translations for a given language
 */
export async function getTranslations(lang: Language = 'en'): Promise<Translations> {
  return loadLanguage(lang);
}

/**
 * Get language from cookie string (server-side)
 */
export function getLangFromCookie(cookieHeader?: string): Language {
  if (!cookieHeader) return 'pt';
  const match = cookieHeader.match(/autonomo_lang=([a-z]{2})/);
  if (!match) return 'pt';
  const lang = match[1] as Language;
  const supported: Language[] = ['pt', 'en', 'fr', 'es'];
  return supported.includes(lang) ? lang : 'pt';
}

export { Language };
